'use client';

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { mapSwapRate } from '@/lib/api/swap/mappers';
import type { SwapRate } from '@/lib/api/swap/models';
import type { SwapRateWsMessage, SwapWsMessage } from '@/lib/api/swap/types';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { WS_ENDPOINTS } from '@/lib/config/websocket';
import { useWebSocket } from '@/lib/hooks/useWebSocket';
import type { AnyWebSocketMessage } from '@/lib/types/websocket';

// --- Rate update pub/sub ---

type SwapRateUpdateListener = (rate: SwapRate) => void;

// How long the socket stays open after the last consumer goes away.
const IDLE_DISCONNECT_MS = 5 * 60 * 1000;

interface SwapRatesContextValue {
  /** Map of "baseCoinId:quoteCoinId" → rate with markup + timestamp */
  rates: Map<string, SwapRate>;
  /** Get rate value for a specific pair by coinIds */
  getRate: (baseCoinId: string, quoteCoinId: string) => number | undefined;
  /** Get full rate data (rate + timestamp) for confirm request */
  getRateData: (baseCoinId: string, quoteCoinId: string) => SwapRate | undefined;
  /** Subscribe to real-time rate updates */
  subscribe: (listener: SwapRateUpdateListener) => () => void;
  /** WebSocket ready state */
  readyState: number;
  /** Register a consumer that needs the socket; returns a release fn. */
  acquire: () => () => void;
}

const SwapRatesContext = createContext<SwapRatesContextValue>({
  rates: new Map(),
  getRate: () => undefined,
  getRateData: () => undefined,
  subscribe: () => () => {},
  readyState: WebSocket.CLOSED,
  acquire: () => () => {},
});

export const useSwapRates = () => useContext(SwapRatesContext);

/** Keeps the shared swap-rates socket connected while `enabled` and mounted. */
export function useSwapRatesConnection(enabled = true) {
  const { acquire } = useSwapRates();
  useEffect(() => {
    if (!enabled) return;
    return acquire();
  }, [enabled, acquire]);
}

// Helper to build rate map key
function rateKey(baseCoinId: string, quoteCoinId: string): string {
  return `${baseCoinId}:${quoteCoinId}`;
}

interface SwapRatesProviderProps {
  children: ReactNode;
}

export function SwapRatesProvider({ children }: SwapRatesProviderProps) {
  const { isAuthenticated } = useAuthContext();
  const [rates, setRates] = useState<Map<string, SwapRate>>(new Map());
  // Driven by consumers, not by mount, so the socket stays closed where unused.
  const [shouldConnect, setShouldConnect] = useState(false);

  const listenersRef = useRef<Set<SwapRateUpdateListener>>(new Set());
  const consumersRef = useRef(0);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const subscribe = useCallback((listener: SwapRateUpdateListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const notifyListeners = useCallback((rate: SwapRate) => {
    listenersRef.current.forEach((listener) => listener(rate));
  }, []);

  const handleMessage = useCallback(
    (msg: AnyWebSocketMessage) => {
      const swapMsg = msg as unknown as SwapWsMessage;

      if (swapMsg.t === 'RATE') {
        const rate = mapSwapRate(swapMsg as SwapRateWsMessage);

        setRates((prev) => {
          const key = rateKey(rate.baseCoinId, rate.quoteCoinId);
          const next = new Map(prev);
          next.set(key, rate);
          return next;
        });

        notifyListeners(rate);
      }
    },
    [notifyListeners],
  );

  const swapRatesSocket = useWebSocket({
    url: WS_ENDPOINTS.swapRates,
    autoConnect: isAuthenticated && shouldConnect,
    reconnect: true,
    onMessage: handleMessage,
  });

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const acquire = useCallback(() => {
    consumersRef.current += 1;
    clearIdleTimer();
    setShouldConnect(true);

    return () => {
      consumersRef.current = Math.max(0, consumersRef.current - 1);
      if (consumersRef.current === 0) {
        idleTimerRef.current = setTimeout(() => {
          idleTimerRef.current = null;
          setShouldConnect(false);
        }, IDLE_DISCONNECT_MS);
      }
    };
  }, [clearIdleTimer]);

  // Drop the connection and cached rates on logout.
  useEffect(() => {
    if (!isAuthenticated) {
      clearIdleTimer();
      consumersRef.current = 0;
      setShouldConnect(false);
      setRates(new Map());
    }
  }, [isAuthenticated, clearIdleTimer]);

  const getRate = useCallback(
    (baseCoinId: string, quoteCoinId: string): number | undefined => {
      return rates.get(rateKey(baseCoinId, quoteCoinId))?.rate;
    },
    [rates],
  );

  const getRateData = useCallback(
    (baseCoinId: string, quoteCoinId: string): SwapRate | undefined => {
      return rates.get(rateKey(baseCoinId, quoteCoinId));
    },
    [rates],
  );

  const ctx = useMemo<SwapRatesContextValue>(
    () => ({
      rates,
      getRate,
      getRateData,
      subscribe,
      readyState: swapRatesSocket.readyState,
      acquire,
    }),
    [rates, getRate, getRateData, subscribe, swapRatesSocket.readyState, acquire],
  );

  return <SwapRatesContext.Provider value={ctx}>{children}</SwapRatesContext.Provider>;
}
